import { createRoute, useNavigate, Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { z } from 'zod';
import { authedLayoutRoute } from './authed.js';
import { createEmployee } from '../api/employees.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { UpgradeCta } from '../components/UpgradeCta.js';
import { socialSecurityLabelForCountry, taxIdLabelForCountry } from '@kora/types';
import type { CountryCode } from '@kora/types';

/**
 * /employees/new — create form. Mirrors the server's CreateEmployeeInput
 * Zod schema field-for-field. The server re-validates — these client
 * messages are for fast feedback.
 *
 * On success: invalidate the ['employees','list'] query so the list page
 * reflects the new row on next view, then navigate to the detail page for
 * the new employee.
 */

const EmployeeForm = z
  .object({
    employeeNumber: z
      .string()
      .min(3)
      .max(32)
      .regex(
        /^[A-Za-z0-9][A-Za-z0-9-]*$/,
        'Start with a letter/digit; use only letters, digits, hyphens',
      ),
    firstName: z.string().min(1).max(100),
    middleName: z.string().max(100).or(z.literal('')).optional(),
    lastName: z.string().min(1).max(100),
    email: z.string().email().max(255).or(z.literal('')).optional(),
    phone: z.string().min(4).max(30).or(z.literal('')).optional(),
    gender: z
      .enum(['male', 'female', 'non_binary', 'other', 'prefer_not_to_say'])
      .or(z.literal(''))
      .optional(),
    countryCode: z.enum(['NG', 'GH', 'CD']),
    stateCode: z
      .string()
      .regex(/^[A-Z]{2}$/, 'Two uppercase letters')
      .or(z.literal(''))
      .optional(),
    taxIdentifier: z.string().max(50).or(z.literal('')).optional(),
    socialSecurityId: z.string().max(50).or(z.literal('')).optional(),
    hireDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    // Tier 1 HRIS fields. All optional — operators populate over time.
    dateOfBirth: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
      .or(z.literal(''))
      .optional(),
    homeAddress: z.string().max(500).or(z.literal('')).optional(),
    nokName: z.string().max(100).or(z.literal('')).optional(),
    nokRelationship: z.string().max(50).or(z.literal('')).optional(),
    nokPhone: z.string().max(30).or(z.literal('')).optional(),
    nokEmail: z.string().email().max(255).or(z.literal('')).optional(),
    nokAddress: z.string().max(500).or(z.literal('')).optional(),
    // Platform HR fields. All optional — every serious HR product has
    // them, but operators may not have the data on day one.
    department: z.string().max(100).or(z.literal('')).optional(),
    position: z.string().max(100).or(z.literal('')).optional(),
    employmentType: z
      .enum(['full_time', 'part_time', 'contract', 'intern'])
      .or(z.literal(''))
      .optional(),
    // ADR 0010 — payroll model (statutory regime). Required enum; the form's
    // defaultValues seed 'local_employee'. Orthogonal to employmentType.
    payrollModel: z.enum(['local_employee', 'international_remote']),
    workLocation: z.string().max(100).or(z.literal('')).optional(),
    bankSwiftCode: z
      .string()
      .regex(
        /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
        '8 or 11 chars: 6 letters + 2 alphanum + optional 3 alphanum',
      )
      .or(z.literal(''))
      .optional(),
    // Bank details for salary credit (bank-export feature). All optional; the
    // bank-export coverage check (at download time) is what enforces population.
    bankName: z.string().max(100).or(z.literal('')).optional(),
    bankAccountNumber: z.string().max(34).or(z.literal('')).optional(),
    bankAccountName: z.string().max(150).or(z.literal('')).optional(),
  })
  .refine((v) => v.countryCode !== 'NG' || (v.stateCode && v.stateCode.length > 0), {
    message: 'NG employees require a state code (e.g. LA for Lagos)',
    path: ['stateCode'],
  })
  // Next-of-kin is "all-or-nothing on the basics" — if you start filling
  // it, name + relationship + phone are the minimum to be useful in an
  // emergency. Email/address are still optional.
  .refine(
    (v) => {
      const any = v.nokName || v.nokRelationship || v.nokPhone || v.nokEmail || v.nokAddress;
      if (!any) return true;
      return Boolean(v.nokName && v.nokRelationship && v.nokPhone);
    },
    {
      message: 'If filling next of kin, name + relationship + phone are required',
      path: ['nokName'],
    },
  );
type EmployeeForm = z.infer<typeof EmployeeForm>;

export const employeeNewRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/employees/new',
  component: EmployeeNewPage,
});

function EmployeeNewPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<EmployeeForm>({
    resolver: zodResolver(EmployeeForm),
    defaultValues: {
      employeeNumber: '',
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      gender: '',
      countryCode: 'NG',
      stateCode: '',
      taxIdentifier: '',
      socialSecurityId: '',
      hireDate: '',
      dateOfBirth: '',
      homeAddress: '',
      nokName: '',
      nokRelationship: '',
      nokPhone: '',
      nokEmail: '',
      nokAddress: '',
      department: '',
      position: '',
      employmentType: '',
      payrollModel: 'local_employee',
      workLocation: '',
      bankSwiftCode: '',
      bankName: '',
      bankAccountNumber: '',
      bankAccountName: '',
    },
  });

  const country = watch('countryCode');

  const mutation = useMutation({
    mutationFn: createEmployee,
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['employees', 'list'] });
      await navigate({ to: '/employees/$id', params: { id: created.id } });
    },
  });

  async function onSubmit(values: EmployeeForm): Promise<void> {
    setServerError(null);
    try {
      // Strip empty-string optionals to match the server's `.optional()`
      // (empty string ≠ omitted for Zod optionals on the server).
      const nokFilled = Boolean(
        values.nokName && values.nokRelationship && values.nokPhone,
      );
      const payload = {
        employeeNumber: values.employeeNumber,
        firstName: values.firstName,
        lastName: values.lastName,
        countryCode: values.countryCode,
        hireDate: values.hireDate,
        ...(values.middleName ? { middleName: values.middleName } : {}),
        ...(values.email ? { email: values.email } : {}),
        ...(values.phone ? { phone: values.phone } : {}),
        ...(values.gender ? { gender: values.gender } : {}),
        ...(values.stateCode ? { stateCode: values.stateCode } : {}),
        ...(values.taxIdentifier ? { taxIdentifier: values.taxIdentifier } : {}),
        ...(values.socialSecurityId ? { socialSecurityId: values.socialSecurityId } : {}),
        ...(values.dateOfBirth ? { dateOfBirth: values.dateOfBirth } : {}),
        ...(values.homeAddress ? { homeAddress: values.homeAddress } : {}),
        ...(values.department ? { department: values.department } : {}),
        ...(values.position ? { position: values.position } : {}),
        ...(values.employmentType ? { employmentType: values.employmentType } : {}),
        // ADR 0010 — always sent (required, defaults to local_employee).
        payrollModel: values.payrollModel,
        ...(values.workLocation ? { workLocation: values.workLocation } : {}),
        ...(values.bankSwiftCode ? { bankSwiftCode: values.bankSwiftCode } : {}),
        ...(values.bankName ? { bankName: values.bankName } : {}),
        ...(values.bankAccountNumber ? { bankAccountNumber: values.bankAccountNumber } : {}),
        ...(values.bankAccountName ? { bankAccountName: values.bankAccountName } : {}),
        ...(nokFilled
          ? {
              nextOfKin: {
                name: values.nokName!,
                relationship: values.nokRelationship!,
                phoneNumber: values.nokPhone!,
                ...(values.nokEmail ? { email: values.nokEmail } : {}),
                ...(values.nokAddress ? { address: values.nokAddress } : {}),
              },
            }
          : {}),
      };
      await mutation.mutateAsync(payload);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string; key?: string } | null) : null;
      if (
        err instanceof ApiError &&
        err.status === 402 &&
        body?.error === 'QUOTA_REACHED' &&
        body?.key === 'max_employees'
      ) {
        // Employee cap reached (Phase D-4). Surface the server message and offer
        // the upgrade path. The employee is NOT created (the 402 fired pre-handler).
        setServerError(err.message);
        setShowUpgrade(true);
      } else if (err instanceof ApiError && err.status === 409) {
        setServerError('An employee with that employee number already exists.');
      } else if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Try again.');
      }
    }
  }

  return (
    <section className="p-8 space-y-6 max-w-2xl">
      <header className="space-y-2">
        <nav className="text-xs text-gray-500">
          <Link to="/employees" className="hover:underline">
            Employees
          </Link>{' '}
          / New
        </nav>
        <h1 className="font-serif text-3xl">Add employee</h1>
        <p className="text-sm text-gray-400">
          Required fields are marked with an asterisk. Everything else can be filled in later.
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            id="employeeNumber"
            label="Employee # *"
            placeholder="EMP-001"
            error={errors.employeeNumber?.message}
            inputProps={register('employeeNumber')}
          />
          <Field
            id="hireDate"
            label="Hire date *"
            type="date"
            error={errors.hireDate?.message}
            inputProps={register('hireDate')}
          />
          <Field
            id="firstName"
            label="First name *"
            error={errors.firstName?.message}
            inputProps={register('firstName')}
          />
          <Field
            id="middleName"
            label="Middle name"
            error={errors.middleName?.message}
            inputProps={register('middleName')}
          />
          <Field
            id="lastName"
            label="Last name *"
            error={errors.lastName?.message}
            inputProps={register('lastName')}
          />
          <SelectField
            id="gender"
            label="Gender"
            error={errors.gender?.message}
            inputProps={register('gender')}
            options={[
              { value: '', label: '— select —' },
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
              { value: 'non_binary', label: 'Non-binary' },
              { value: 'other', label: 'Other' },
              { value: 'prefer_not_to_say', label: 'Prefer not to say' },
            ]}
          />
          <Field
            id="email"
            label="Email"
            type="email"
            error={errors.email?.message}
            inputProps={register('email')}
          />
          <Field
            id="phone"
            label="Phone"
            error={errors.phone?.message}
            inputProps={register('phone')}
          />
          <SelectField
            id="countryCode"
            label="Country *"
            error={errors.countryCode?.message}
            inputProps={register('countryCode')}
            options={[
              { value: 'NG', label: 'Nigeria (NG)' },
              { value: 'GH', label: 'Ghana (GH)' },
              { value: 'CD', label: 'DR Congo (CD)' },
            ]}
          />
          <Field
            id="stateCode"
            label={country === 'NG' ? 'State code * (e.g. LA)' : 'State code'}
            placeholder="LA"
            error={errors.stateCode?.message}
            inputProps={register('stateCode')}
          />
          <Field
            id="taxIdentifier"
            label={taxIdLabelForCountry(country as CountryCode)}
            error={errors.taxIdentifier?.message}
            inputProps={register('taxIdentifier')}
          />
          <Field
            id="socialSecurityId"
            label={socialSecurityLabelForCountry(country as CountryCode)}
            error={errors.socialSecurityId?.message}
            inputProps={register('socialSecurityId')}
          />
        </div>

        {/* ------------------------------------------------------------ */}
        {/* HRIS extras — Mokoafrika asked for these to consolidate off  */}
        {/* Bamboo HR. Optional fields, populate over time.              */}
        {/* ------------------------------------------------------------ */}
        <fieldset className="space-y-4 rounded border border-gray-800 bg-gray-950 p-4">
          <legend className="px-2 text-xs uppercase tracking-wide text-gray-400">
            Personal details
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="dateOfBirth"
              label="Date of birth"
              type="date"
              error={errors.dateOfBirth?.message}
              inputProps={register('dateOfBirth')}
            />
            <Field
              id="homeAddress"
              label="Home address"
              placeholder="Street, city, postal"
              error={errors.homeAddress?.message}
              inputProps={register('homeAddress')}
            />
          </div>
        </fieldset>

        {/* ------------------------------------------------------------ */}
        {/* Employment details — org-structure HRIS basics that every    */}
        {/* serious HR product has. All optional; populated over time.   */}
        {/* ------------------------------------------------------------ */}
        <fieldset className="space-y-4 rounded border border-gray-800 bg-gray-950 p-4">
          <legend className="px-2 text-xs uppercase tracking-wide text-gray-400">
            Employment details
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="department"
              label="Department"
              placeholder="Engineering"
              error={errors.department?.message}
              inputProps={register('department')}
            />
            <Field
              id="position"
              label="Position / Job title"
              placeholder="Senior Engineer"
              error={errors.position?.message}
              inputProps={register('position')}
            />
            <SelectField
              id="employmentType"
              label="Work arrangement"
              error={errors.employmentType?.message}
              inputProps={register('employmentType')}
              options={[
                { value: '', label: '— select —' },
                { value: 'full_time', label: 'Full-time' },
                { value: 'part_time', label: 'Part-time' },
                { value: 'contract', label: 'Contract' },
                { value: 'intern', label: 'Intern' },
              ]}
            />
            <SelectField
              id="payrollModel"
              label="Payroll model"
              error={errors.payrollModel?.message}
              inputProps={register('payrollModel')}
              options={[
                { value: 'local_employee', label: 'Local employee' },
                { value: 'international_remote', label: 'International remote' },
              ]}
            />
            <p className="text-xs text-gray-500">
              How payroll runs for this employee. Choose &ldquo;International
              remote&rdquo; for full-time employees who live and work in a
              country different from this organization&rsquo;s primary country —
              no statutory deductions are applied and they&rsquo;re excluded from
              country filings.
            </p>
            <Field
              id="workLocation"
              label="Work location"
              placeholder="Lagos HQ / Remote"
              error={errors.workLocation?.message}
              inputProps={register('workLocation')}
            />
          </div>
        </fieldset>

        {/* ------------------------------------------------------------ */}
        {/* Banking — for cross-border payments, SWIFT/BIC matters.       */}
        {/* ------------------------------------------------------------ */}
        <fieldset className="space-y-4 rounded border border-gray-800 bg-gray-950 p-4">
          <legend className="px-2 text-xs uppercase tracking-wide text-gray-400">
            Banking
          </legend>
          <p className="text-xs text-gray-500">
            Required to receive salary payments. SWIFT/BIC is for international
            wire transfers; domestic payments don&apos;t require it.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="bankName"
              label="Bank name"
              placeholder="Equity BCDC"
              error={errors.bankName?.message}
              inputProps={register('bankName')}
            />
            <Field
              id="bankAccountNumber"
              label="Account number"
              placeholder="0001234567"
              error={errors.bankAccountNumber?.message}
              inputProps={register('bankAccountNumber')}
            />
            <Field
              id="bankAccountName"
              label="Account name"
              placeholder="As registered with the bank"
              error={errors.bankAccountName?.message}
              inputProps={register('bankAccountName')}
            />
            <Field
              id="bankSwiftCode"
              label="SWIFT / BIC code"
              placeholder="GTBINGLA"
              error={errors.bankSwiftCode?.message}
              inputProps={register('bankSwiftCode')}
            />
          </div>
        </fieldset>

        <fieldset className="space-y-4 rounded border border-gray-800 bg-gray-950 p-4">
          <legend className="px-2 text-xs uppercase tracking-wide text-gray-400">
            Next of kin (emergency contact)
          </legend>
          <p className="text-xs text-gray-500">
            Optional. If filling, name + relationship + phone are the
            minimum so it's actually usable in an emergency.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="nokName"
              label="Full name"
              error={errors.nokName?.message}
              inputProps={register('nokName')}
            />
            <Field
              id="nokRelationship"
              label="Relationship"
              placeholder="Spouse / Parent / Sibling"
              error={errors.nokRelationship?.message}
              inputProps={register('nokRelationship')}
            />
            <Field
              id="nokPhone"
              label="Phone"
              error={errors.nokPhone?.message}
              inputProps={register('nokPhone')}
            />
            <Field
              id="nokEmail"
              label="Email (optional)"
              type="email"
              error={errors.nokEmail?.message}
              inputProps={register('nokEmail')}
            />
            <div className="sm:col-span-2">
              <Field
                id="nokAddress"
                label="Address (optional)"
                error={errors.nokAddress?.message}
                inputProps={register('nokAddress')}
              />
            </div>
          </div>
        </fieldset>

        {serverError ? (
          <Alert
            variant="error"
            action={showUpgrade ? <UpgradeCta label="Upgrade your plan" className="shrink-0 font-medium underline hover:no-underline" /> : undefined}
          >
            {serverError}
          </Alert>
        ) : null}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={isSubmitting || mutation.isPending}
            className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {isSubmitting || mutation.isPending ? 'Creating...' : 'Create employee'}
          </button>
          <Link
            to="/employees"
            className="rounded border border-gray-700 px-4 py-2 text-sm hover:bg-gray-900"
          >
            Cancel
          </Link>
        </div>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Reusable field components (scoped to this route)
// ---------------------------------------------------------------------------

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<EmployeeForm>>['register']>;
}

function Field({ id, label, type = 'text', placeholder, error, inputProps }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

interface SelectFieldProps extends Omit<FieldProps, 'type' | 'placeholder'> {
  options: Array<{ value: string; label: string }>;
}

function SelectField({ id, label, error, inputProps, options }: SelectFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <select
        id={id}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
        {...inputProps}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
